import { DataStatCategoriesForm, StatItems } from '@/api-client/metav6/model';
import { convertToStatDataType, StatInfo } from '../components/organisms/statWindow';
import { mapChildCategoryWithParent } from '../components/organisms/statWindow/statContents/statContents.utils';
import {
  CUSTOM_QUERY_MANAGER_CATEGORY,
  CUSTOM_QUERY_MANAGER_CATEGORY_FOR_API,
  CUSTOM_STAT_CATEGORY_PREFIX,
  CUSTOM_USER_INTEGRATION_CATEGORY_PREFIX,
} from './statInfo.define';

type ParentCategory = string;
type ChildCategory = string;
type StatId = string;

export const parseStatItems = (categoryList: DataStatCategoriesForm[], statItems: StatItems) => {
  const infoByStatIdAndCategory: Map<ChildCategory, Map<StatId, StatInfo>> = new Map(
    categoryList
      .map(({ pcategory, categories }) =>
        categories
          ? [...categories].map((category) => ({
              category,
              pcategory,
            }))
          : [],
      )
      .flat()
      .map(({ pcategory, category }) => [
        mapChildCategoryWithParent({
          parentCategory: pcategory ?? '',
          childCategory: category ?? '',
        }),
        new Map(),
      ]),
  );

  const infoByCategoryInfo: Map<ParentCategory, Map<ChildCategory, StatInfo[]>> = new Map(
    categoryList.map(({ pcategory, categories }) => [
      pcategory ?? '',
      new Map(
        (categories ? [...categories] : []).map((category) => [
          mapChildCategoryWithParent({
            parentCategory: pcategory ?? '',
            childCategory: category ?? '',
          }),
          [],
        ]),
      ),
    ]),
  );

  const info: Map<StatId, StatInfo> = new Map();

  const { dataIds, favoriteDataIds } = statItems;

  dataIds?.forEach(
    ({
      dataId,
      displayName,
      popular,
      tag,
      recommend,
      category,
      pcategory,
      unit,
      dataType,
      dataKind,
      args,
      queryManagerUseAlert,
      statOption,
      frontVersion,
      customPCategory,
      customCCategory,
    }) => {
      if (
        category != null &&
        dataId != null &&
        pcategory != null &&
        displayName != null &&
        dataType != null
      ) {
        const tagList = tag?.split(',').filter((tagData) => !!tagData) ?? [];
        const statInfo: StatInfo = {
          parentCategory: pcategory,
          childCategory: category,
          id: dataId,
          name: displayName,
          tagList: tagList.length > 0 ? [...tagList] : [],
          favorite: !!favoriteDataIds?.includes(dataId),
          popular: popular ?? 0,
          recommend: !!recommend,
          dataType: dataType
            .split(',')
            .filter((type) => !!type)
            .map(convertToStatDataType),
          unit,
          dataKind,
          ableForAlert: queryManagerUseAlert,
          tableAlarm: statOption?.tableAlarm ?? false,
          arguments: args?.filter(
            (arg): arg is NonNullable<StatInfo['arguments']>[0] => !!arg.name,
          ),
          frontVersion,
          customPCategory,
          customCCategory,
        };

        info.set(dataId, statInfo);

        const childCategory = mapChildCategoryWithParent({
          parentCategory: pcategory,
          childCategory: category,
        });

        infoByStatIdAndCategory.get(childCategory)?.set(dataId, statInfo);
        const statInfoList = infoByCategoryInfo.get(pcategory)?.get(childCategory);
        if (statInfoList) {
          statInfoList.push(statInfo);
        }
      }
    },
  );

  return { infoByStatIdAndCategory, infoByCategoryInfo, info };
};

/**
 * 사용자 정의 지표의 parentCategory인지 확인
 * @param parentCategory - 부모 카테고리
 */
export const isCustomStatParentCategory = (parentCategory: string): boolean =>
  parentCategory.startsWith(CUSTOM_STAT_CATEGORY_PREFIX) ||
  parentCategory.startsWith(CUSTOM_USER_INTEGRATION_CATEGORY_PREFIX);

/** QM stat은 맵에서 항상 'custom' 키로 저장되므로, 맵 조회 시 childCategory를 정규화한다. */
export const normalizeQmCategory = (parentCategory: string, childCategory: string): string => {
  return parentCategory.startsWith(CUSTOM_QUERY_MANAGER_CATEGORY)
    ? CUSTOM_QUERY_MANAGER_CATEGORY_FOR_API
    : childCategory;
};
